import React, { useEffect, useState } from "react";
import {
  Paper,
  Divider,
  Box,
  Typography,
  TableRow,
  TableCell,
  Grid,
  Checkbox,
} from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/RenovationPdf";
import { formatAmount, getCommaOut } from "../../../utils/hooks/dataHandler";
import JustTable from "../../utils/Table";
import ScheduleHeader from "../../App/ScheduleHeader";
import { JustDropDown } from "../../utils/Form/Dropdown";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import SectionBox from "../utils/SectionBox";
import ScheduleContainer from "../utils/ScheduleContainer";

const claimModeOptions = ["1x3 years claim", "2x3 years claim"];

const RENOVATION_LARGE_LAYOUT_VIEWPORT_THRESHOLD = 1400;

const RENOVATION_CLAIM_TABLE_DISPLAY_CONFIG = {
  sdg: {
    default: {
      actionColumnWidth: "64px",
      tableMinWidth: "1410px",
      columns: {
        YA: { width: "100px", inputWidth: 92 },
        costCF: { width: "160px" },
        costIncurred: { width: "155px" },
        deduction: { width: "170px" },
        qualifyingCost: { width: "155px" },
        qualifyingCostBF: { width: "155px" },
        start3Year: { width: "82px" },
        yearsBF: { width: "100px" },
        yearsCF: { width: "100px" },
        yearsClaim: { fullWidth: true, width: "80px" },
      },
    },
    large: {
      actionColumnWidth: "56px",
      tableMinWidth: "1320px",
      columns: {
        YA: { width: "100px", inputWidth: 96 },
        costCF: { width: "140px" },
        costIncurred: { width: "140px" },
        deduction: { width: "150px" },
        qualifyingCost: { width: "140px" },
        qualifyingCostBF: { width: "140px" },
        start3Year: { width: "76px" },
        yearsBF: { width: "92px" },
        yearsCF: { width: "92px" },
        yearsClaim: { fullWidth: true, width: "80px" },
      },
    },
  },
  nonSdg: {
    default: {
      actionColumnWidth: "64px",
      tableMinWidth: "1780px",
      columns: {
        YA: { width: "100px", inputWidth: 92 },
        costCF: { width: "155px" },
        costIncurred: { width: "150px" },
        costIncurredFunc: { width: "150px" },
        deduction: { width: "165px" },
        exRate: { width: "112px" },
        qualifyingCost: { width: "160px" },
        qualifyingCostBF: { width: "155px" },
        qualifyingCostFunc: { width: "160px" },
        start3Year: { width: "82px" },
        yearsBF: { width: "100px" },
        yearsCF: { width: "100px" },
        yearsClaim: { fullWidth: true, width: "80px" },
      },
    },
    large: {
      actionColumnWidth: "56px",
      tableMinWidth: "1700px",
      columns: {
        YA: { width: "100px", inputWidth: 96 },
        costCF: { width: "145px" },
        costIncurred: { width: "140px" },
        costIncurredFunc: { width: "140px" },
        deduction: { width: "155px" },
        exRate: { width: "104px" },
        qualifyingCost: { width: "150px" },
        qualifyingCostBF: { width: "145px" },
        qualifyingCostFunc: { width: "150px" },
        start3Year: { width: "76px" },
        yearsBF: { width: "92px" },
        yearsCF: { width: "92px" },
        yearsClaim: { fullWidth: true, width: "80px" },
      },
    },
  },
};

const getViewportWidth = () => {
  if (typeof window === "undefined")
    return RENOVATION_LARGE_LAYOUT_VIEWPORT_THRESHOLD;

  return (
    window.visualViewport?.width ||
    window.innerWidth ||
    document.documentElement?.clientWidth ||
    RENOVATION_LARGE_LAYOUT_VIEWPORT_THRESHOLD
  );
};

const shouldUseLargeRenovationLayout = () =>
  getViewportWidth() < RENOVATION_LARGE_LAYOUT_VIEWPORT_THRESHOLD;

const getRenovationClaimTableDisplayConfig = (isSdg, largeLayout) =>
  RENOVATION_CLAIM_TABLE_DISPLAY_CONFIG[isSdg ? "sdg" : "nonSdg"][
    largeLayout ? "large" : "default"
  ];

const applyRenovationColumnDisplayConfig = (columns, displayConfig) =>
  columns.map((column) => ({
    ...column,
    ...(displayConfig.columns[column.id] || {}),
  }));

const headerSDGSettings = [
  { value: "YA", align: "left" },
  { value: "Start of 3 Year Claim", align: "center" },
  { value: "Renovation Cost Incurred", align: "center" },
  { value: "Qualifying R&R cost", align: "center" },
  { value: "Qualifying R&R cost b/f", align: "center" },
  { value: "No. of Years of Claim", align: "center" },
  { value: "No. of Years b/f", align: "center" },
  { value: "Current YA S14N Deduction", align: "center" },
  { value: "Qualifying R&R Cost c/f", align: "center" },
  { value: "No. of Years c/f", align: "center" },
];

const headerNoSDGSettings = [
  { value: "YA", align: "left" },
  { value: "Start of 3 Year Claim", align: "center" },
  { value: "Renovation cost (func. $)", align: "center" },
  { value: "Ex. rate", align: "center" },
  { value: "Renovation cost (S$)", align: "center" },
  { value: "Qualifying R&R cost (S$)", align: "center" },
  { value: "Qualifying R&R cost (func. $)", align: "center" },
  { value: "Qualifying R&R cost b/f", align: "center" },
  { value: "No. of Years of Claim", align: "center" },
  { value: "No. of Years b/f", align: "center" },
  { value: "Current YA S14N Deduction", align: "center" },
  { value: "Qualifying R&R Cost c/f", align: "center" },
  { value: "No. of Years c/f", align: "center" },
];

const columnSDGSettings = [
  { type: "year_text", id: "YA", maxYear: new Date().getFullYear() },
  { type: "checkbox", id: "start3Year" },
  { type: "number_text", id: "costIncurred", align: "right" },
  { type: "", id: "qualifyingCost", align: "right" },
  { type: "", id: "qualifyingCostBF", align: "right" },
  { type: "text", id: "yearsClaim", align: "center" },
  { type: "", id: "yearsBF", align: "center" },
  { type: "", id: "deduction", align: "right" },
  { type: "", id: "costCF", align: "right" },
  { type: "", id: "yearsCF", align: "center" },
];

const columnNoSDGSettings = [
  { type: "year_text", id: "YA", maxYear: new Date().getFullYear() },
  { type: "checkbox", id: "start3Year" },
  { type: "number_text", id: "costIncurredFunc", align: "right" },
  { type: "number_decimal", id: "exRate", align: "right" },
  { type: "number_text", id: "costIncurred", align: "right" },
  { type: "", id: "qualifyingCost", align: "right" },
  { type: "", id: "qualifyingCostFunc", align: "right" },
  { type: "", id: "qualifyingCostBF", align: "right" },
  { type: "text", id: "yearsClaim", align: "center" },
  { type: "", id: "yearsBF", align: "center" },
  { type: "", id: "deduction", align: "right" },
  { type: "", id: "costCF", align: "right" },
  { type: "", id: "yearsCF", align: "center" },
];

const RenovationTable = ({
  rows,
  isSdg,
  selectedYears,
  handleInputChange,
  handleCheckboxChange,
  totalCostIncurred,
  totalQualifyingCost,
  totalQualifyingCostBF,
  totalDeduction,
  totalCostCF,
  addRow,
  removeRow,
  canEdit = true,
  largeLayout = false,
}) => {
  const tableDisplayConfig = getRenovationClaimTableDisplayConfig(
    isSdg,
    largeLayout
  );
  const columnSettings = applyRenovationColumnDisplayConfig(
    isSdg ? columnSDGSettings : columnNoSDGSettings,
    tableDisplayConfig
  );

  return (
    <JustTable
      headers={isSdg ? headerSDGSettings : headerNoSDGSettings}
      rows={rows}
      selectedYears={selectedYears}
      columnSettings={columnSettings}
      handleInputChange={handleInputChange}
      handleCheckboxChange={handleCheckboxChange}
      handleDeleteRow={removeRow}
      canEdit={canEdit}
      actionColumnWidth={tableDisplayConfig.actionColumnWidth}
      tableSx={{
        minWidth: tableDisplayConfig.tableMinWidth,
        tableLayout: "fixed",
        "& .MuiTableCell-root": {
          px: largeLayout ? 1 : 1.25,
          py: largeLayout ? 1 : 1.25,
          verticalAlign: "middle",
        },
        "& .MuiTableHead .MuiTableCell-root": {
          fontSize: largeLayout ? "0.72rem" : "0.76rem",
          fontWeight: 600,
          lineHeight: 1.2,
          whiteSpace: "normal",
          wordBreak: "normal",
        },
        "& .MuiTableBody .MuiTableCell-root": {
          overflow: "hidden",
        },
      }}
    >
      <TableRow>
        <TableCell colSpan={isSdg ? 2 : 4} />
        <TableCell align='right' sx={{ fontWeight: 500 }}>
          {formatAmount(totalCostIncurred)}
        </TableCell>
        <TableCell align='right' sx={{ fontWeight: 500 }}>
          {formatAmount(totalQualifyingCost)}
        </TableCell>
        {!isSdg && <TableCell />}
        <TableCell align='right' sx={{ fontWeight: 500 }}>
          {formatAmount(totalQualifyingCostBF)}
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell align='right' sx={{ fontWeight: 500 }}>
          {formatAmount(totalDeduction)}
        </TableCell>
        <TableCell align='right' sx={{ fontWeight: 500 }}>
          {formatAmount(totalCostCF)}
        </TableCell>
        <TableCell colSpan={canEdit ? 2 : 1}></TableCell>
      </TableRow>
    </JustTable>
  );
};

const RenovationSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const cappedAmount = 300000;
  const currentYear = company.yearOfAssessment;
  const [selectedYears, setSelectedYears] = useState([]);

  const [rennovationRows, setRennovationRows] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowsSecondTable, setRowsSecondTable] = useState([]);
  const [mode, setMode] = useState("1x3 years claim");
  const [isSdg, setIsSdg] = useState(true);
  const [totalRennovations, setTotalRennovations] = useState(0);
  const [totalQualifyingCost, setTotalQualifyingCost] = useState(0);
  const [totalQualifyingCostBF, setTotalQualifyingCostBF] = useState(0);
  const [totalCostIncurred, setTotalCostIncurred] = useState(0);
  const [totalDeduction, setTotalDeduction] = useState(0);
  const [totalCostCF, setTotalCostCF] = useState(0);
  const [totalSecondQualifyingCost, setTotalSecondQualifyingCost] = useState(0);
  const [totalSecondQualifyingCostBF, setTotalSecondQualifyingCostBF] =
    useState(0);
  const [totalSecondCostIncurred, setTotalSecondCostIncurred] = useState(0);
  const [totalSecondDeduction, setTotalSecondDeduction] = useState(0);
  const [totalSecondCostCF, setTotalSecondCostCF] = useState(0);
  const [useLargeRenovationLayout, setUseLargeRenovationLayout] = useState(() =>
    shouldUseLargeRenovationLayout()
  );

  useEffect(() => {
    const updateLayoutMode = () => {
      setUseLargeRenovationLayout(shouldUseLargeRenovationLayout());
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  const calculateYearsBF = (
    newRows,
    setNewRows,
    setDeduction,
    setCostCF,
    setQualifyingCostBF,
    isSdg
  ) => {
    newRows.map((row) => {
      let yearsClaim = row["yearsClaim"].value;
      let yearsBF = Math.max(yearsClaim - (currentYear - row["YA"].value), 0);

      row["yearsBF"].value = yearsBF;

      if (yearsBF == 0) row["qualifyingCostBF"].value = 0;
      else {
        let base = isSdg
          ? row["qualifyingCost"].value
          : row["qualifyingCostFunc"].value;
        let cost = parseFloat(getCommaOut(base)) || 0;
        row["qualifyingCostBF"].value = formatAmount(
          cost - Math.round(cost / yearsClaim) * (yearsClaim - yearsBF)
        );
      }

      if (yearsBF == 0 || yearsBF == 1) {
        row["deduction"].value = row["qualifyingCostBF"].value;
      } else if (yearsBF > 1) {
        row["deduction"].value = formatAmount(
          parseFloat(getCommaOut(row["qualifyingCostBF"].value)) / yearsBF
        );
      } else row["deduction"].value = 0;

      row["costCF"].value = formatAmount(
        getCommaOut(row["qualifyingCostBF"].value) -
          getCommaOut(row["deduction"].value)
      );

      row["yearsCF"].value = yearsBF == 0 ? 0 : yearsBF - 1;
    });
    setDeduction(
      newRows.reduce(
        (sum, row) =>
          sum + parseFloat(getCommaOut(row["deduction"].value) || 0),
        0
      )
    );
    setCostCF(
      newRows.reduce(
        (sum, row) => sum + parseFloat(getCommaOut(row["costCF"].value) || 0),
        0
      )
    );
    setQualifyingCostBF(
      newRows.reduce(
        (sum, row) =>
          sum + parseFloat(getCommaOut(row["qualifyingCostBF"].value) || 0),
        0
      )
    );
    setNewRows(newRows);
  };

  const calculateCostIncurred = (newRows, setCostIncurred) => {
    setCostIncurred(
      newRows.reduce(
        (sum, row) =>
          sum + parseFloat(getCommaOut(row.costIncurred.value) || 0),
        0
      )
    );
  };

  const calculateQualifyingCost = (
    newRows,
    setNewRows,
    setQualifyingCost,
    isSdg
  ) => {
    let sum = 0;
    newRows
      .sort((a, b) => {
        return a.YA.value - b.YA.value;
      })
      .map((row) => {
        let qualifyingCost = 0;
        qualifyingCost = Math.min(
          getCommaOut(row["costIncurred"].value),
          cappedAmount - sum
        );
        row["qualifyingCost"].value = formatAmount(qualifyingCost);

        if (!isSdg) {
          let exRate = row["exRate"].value;
          if (!exRate) exRate = 1;
          let qualifyingCostFunc = qualifyingCost / exRate;
          if (!row["qualifyingCostFunc"]) {
            row["qualifyingCostFunc"] = {
              value: "",
            };
          }

          row["qualifyingCostFunc"].value = formatAmount(qualifyingCostFunc);
        }
        sum += qualifyingCost;
      });
    setNewRows(newRows.sort((a, b) => b.YA.value - a.YA.value));
    setQualifyingCost(sum);
  };

  const handleRennovationInputChange = (value, { index, field }) => {
    const updatedRows = [...rennovationRows];
    updatedRows[index][field] = value;
    setRennovationRows(updatedRows);
  };

  const handleGenericInputChange = (
    index,
    field,
    value,
    data,
    setData,
    otherData,
    setTotalCostIncurred,
    setTotalQualifyingCost,
    setTotalDeduction,
    setTotalCostCF,
    setTotalQualifyingCostBF
  ) => {
    const updatedRows = [...data];

    if (["costIncurred", "costIncurredFunc"].includes(field)) {
      updatedRows[index][field].historyAmount = value;
      updatedRows[index][field].value = value.reduce(
        (sum, num) => sum + num,
        0
      );
    } else {
      updatedRows[index][field].value = value;
    }

    if (!isSdg && ["exRate", "costIncurredFunc"].includes(field)) {
      let newCostInc = Math.round(
        updatedRows[index]["costIncurredFunc"].value *
          updatedRows[index]["exRate"].value
      );
      updatedRows[index]["costIncurred"].value = newCostInc;
      updatedRows[index]["costIncurred"].historyAmount = [newCostInc];
    }

    setData(updatedRows);

    // Combine both tables for selected years
    setSelectedYears([...updatedRows, ...otherData].map((row) => row.YA.value));

    // Recalculate values
    calculateCostIncurred(updatedRows, setTotalCostIncurred);
    calculateQualifyingCost(
      updatedRows,
      setData,
      setTotalQualifyingCost,
      isSdg
    );
    calculateYearsBF(
      updatedRows,
      setData,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF,
      isSdg
    );
  };

  const handleInputChange = (index, field, value) =>
    handleGenericInputChange(
      index,
      field,
      value,
      rows,
      setRows,
      rowsSecondTable,
      setTotalCostIncurred,
      setTotalQualifyingCost,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF
    );

  const handleSecondRowsInputChange = (index, field, value) =>
    handleGenericInputChange(
      index,
      field,
      value,
      rowsSecondTable,
      setRowsSecondTable,
      rows,
      setTotalSecondCostIncurred,
      setTotalSecondQualifyingCost,
      setTotalSecondDeduction,
      setTotalSecondCostCF,
      setTotalSecondQualifyingCostBF
    );

  const updateCurrentYearCost = (
    newValue,
    year,
    isSdg,
    hasRennovationRows,
    existingData = null
  ) => {
    const updatedRows = [...rows];
    let yearFound = false;

    updatedRows.forEach((row) => {
      if (row["YA"].value == year && hasRennovationRows) {
        if (!isSdg) {
          row["costIncurredFunc"].value = newValue;
          row["costIncurredFunc"].historyAmount = [newValue];
          row["costIncurredFunc"].disabled = true;
        }

        row["costIncurred"].value = newValue;
        row["costIncurred"].historyAmount = [newValue];
        row["costIncurred"].disabled = true;
        row["YA"].disabled = true;
        row["permitToDelete"] = false;
        yearFound = true;
      }
    });

    if (!yearFound && hasRennovationRows) {
      // Check if there's existing data for this year to preserve start3Year state
      const existingYearData = existingData
        ? existingData.find((item) => item.YA === year)
        : null;
      const preservedStart3Year = existingYearData
        ? existingYearData.start3Year
        : false;

      updatedRows.push({
        start3Year: { value: preservedStart3Year },
        qualifyingCost: { value: "" },
        qualifyingCostBF: { value: "" },
        yearsClaim: {
          value: existingYearData ? existingYearData.yearsClaim : 3,
        },
        yearsBF: { value: "" },
        deduction: { value: "" },
        costCF: { value: "" },
        yearsCF: { value: "" },
        YA: { value: year, disabled: true },
        exRate: { value: existingYearData ? existingYearData.exRate : "" },
        costIncurredFunc: isSdg
          ? { value: "", historyAmount: [] }
          : {
              value: newValue,
              historyAmount: [newValue],
              disabled: true,
            },
        costIncurred: {
          value: newValue,
          historyAmount: [newValue],
          disabled: true,
        },
        permitToDelete: false,
      });
    }

    return updatedRows;
  };

  const updateExclusiveCheckbox = (rows, rowIndex, columnId) => {
    return rows.map((row, index) => ({
      ...row,
      [columnId]: {
        ...row[columnId],
        value: index === rowIndex ? !row[columnId].value : false,
      },
    }));
  };

  const handleCheckboxChange = (rowIndex, columnId) => {
    const updatedRows = updateExclusiveCheckbox(rows, rowIndex, columnId);
    setRows(updatedRows);
  };

  const handleSecondRowsCheckboxChange = (rowIndex, columnId) => {
    const updatedRows = updateExclusiveCheckbox(
      rowsSecondTable,
      rowIndex,
      columnId
    );
    setRowsSecondTable(updatedRows);
  };

  const handleSave = () => {
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    saveSchedule(schedule, {
      isSdg: isSdg,
      mode: mode,
      rennovationRows: rennovationRows,
      firstTableRows: rows.map((line) => ({
        ...Object.fromEntries(
          Object.entries(line).map(([key, obj]) => [key, obj?.value ?? ""])
        ),
        costIncurredHistoryAmount: line["costIncurred"].historyAmount,
        costIncurredFuncHistoryAmount: line["costIncurredFunc"].historyAmount,
      })),
      secondTableRows: rowsSecondTable.map((line) => ({
        ...Object.fromEntries(
          Object.entries(line).map(([key, obj]) => [key, obj?.value ?? ""])
        ),
        costIncurredHistoryAmount: line["costIncurred"].historyAmount,
        costIncurredFuncHistoryAmount: line["costIncurredFunc"].historyAmount,
      })),
      totalAdjustment: totalDeduction + totalSecondDeduction,
      doc: JSON.stringify({
        rennovationRows,
        mode,
        isSdg,
        tableRows: rows,
        secondTableRows: rowsSecondTable,
        totalRennovations,
        totalQualifyingCost,
        totalQualifyingCostBF,
        totalCostIncurred,
        totalDeduction,
        totalCostCF,
        totalSecondCostIncurred,
        totalSecondQualifyingCost,
        totalSecondQualifyingCostBF,
        totalSecondDeduction,
        totalSecondCostCF,
        notes,
        reviewNotes,
      }),
    });
  };

  const updateRowCurrencyFields = (row, isSdg, currentYear) => {
    if (!isSdg) {
      row["exRate"].value = 1;
      row["costIncurredFunc"].value = row["costIncurred"].value;
      row["costIncurredFunc"].historyAmount = row["costIncurred"].historyAmount;
      if (row["YA"].value !== currentYear) {
        row["costIncurred"].disabled = true;
      } else {
        row["costIncurredFunc"].disabled = true;
      }
    } else {
      row["costIncurred"].value = row["costIncurredFunc"].value;
      row["costIncurred"].historyAmount = row["costIncurredFunc"].historyAmount;
      if (row["YA"].value !== currentYear) {
        row["costIncurred"].disabled = false;
      }
    }
  };

  const handleFuncCurrencyChange = (checked) => {
    setIsSdg(checked);

    const updatedRows = [...rows].map((row) => {
      const updatedRow = { ...row };
      updateRowCurrencyFields(updatedRow, checked, currentYear);
      return updatedRow;
    });

    const updatedSecondRows = [...rowsSecondTable].map((row) => {
      const updatedRow = { ...row };
      updateRowCurrencyFields(updatedRow, checked, currentYear);
      return updatedRow;
    });

    setRows(updatedRows);
    setRowsSecondTable(updatedSecondRows);

    calculateCostIncurred(updatedRows, setTotalCostIncurred);

    calculateQualifyingCost(
      updatedRows,
      setRows,
      setTotalQualifyingCost,
      checked
    );

    calculateQualifyingCost(
      updatedSecondRows,
      setRowsSecondTable,
      setTotalSecondQualifyingCost,
      checked
    );

    calculateYearsBF(
      updatedRows,
      setRows,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF,
      checked
    );

    calculateYearsBF(
      updatedSecondRows,
      setRowsSecondTable,
      setTotalSecondDeduction,
      setTotalSecondCostCF,
      setTotalSecondQualifyingCostBF,
      checked
    );
  };

  useEffect(() => {
    if (!schedule && !scheduleDetails) return;

    if (schedule) {
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (!scheduleDetails) return;

    setIsSdg(scheduleDetails.isSdg);
    setMode(scheduleDetails.mode);

    const dataRows = scheduleDetails.dataRows;
    if (!dataRows) return;

    const { rennovationRows = [], first = [], second = [] } = dataRows;

    let firstRows = first;
    let secondRows = second;

    const mapRows = (rows) =>
      rows.map((item) => ({
        YA: {
          value: item.YA || "",
          disabled: item.YA === currentYear && rennovationRows.length > 0,
        },
        start3Year: { value: item.start3Year },
        exRate: { value: item.exRate },
        costIncurredFunc: {
          value: item.costIncurredFunc,
          historyAmount: item.costIncurredFuncHistoryAmount || [
            item.costIncurredFunc,
          ],
        },
        costIncurred: {
          value:
            Array.isArray(item.costIncurredHistoryAmount) &&
            item.costIncurredHistoryAmount.length > 0
              ? item.costIncurredHistoryAmount.reduce(
                  (sum, val) => sum + (parseFloat(val) || 0),
                  0
                )
              : item.costIncurred,
          historyAmount: item.costIncurredHistoryAmount || [item.costIncurred],
        },
        qualifyingCost: { value: "" },
        qualifyingCostFunc: { value: "" },
        qualifyingCostBF: { value: "" },
        yearsClaim: { value: item.yearsClaim },
        yearsBF: { value: "" },
        deduction: { value: "" },
        costCF: { value: "" },
        yearsCF: { value: "" },
        permitToDelete: item.YA !== currentYear,
      }));

    if (scheduleDetails.isSdg === undefined) scheduleDetails.isSdg = true;

    const totalRennovations = CommonCalculator.arraySum(rennovationRows);
    setRennovationRows(rennovationRows);
    setTotalRennovations(totalRennovations);

    const curFirstRenoRows = updateCurrentYearCost(
      totalRennovations,
      currentYear,
      scheduleDetails.isSdg,
      rennovationRows.length > 0,
      first
    );

    if (!scheduleDetails.isSdg && curFirstRenoRows.length > 0) {
      let item = first.find((item) => item.YA === currentYear);
      let exRate = 1;
      if (item) exRate = item.exRate;

      // Ensure exRate property exists
      if (!curFirstRenoRows[0]["exRate"]) {
        curFirstRenoRows[0]["exRate"] = { value: exRate || 1 };
      } else {
        curFirstRenoRows[0]["exRate"].value = exRate || 1;
      }

      // Ensure costIncurredFunc property exists
      if (
        curFirstRenoRows[0]["costIncurredFunc"] &&
        curFirstRenoRows[0]["exRate"]
      ) {
        let newCostInc = Math.round(
          curFirstRenoRows[0]["costIncurredFunc"].value *
            curFirstRenoRows[0]["exRate"].value
        );
        if (curFirstRenoRows[0]["costIncurred"]) {
          curFirstRenoRows[0]["costIncurred"].value = newCostInc;
          curFirstRenoRows[0]["costIncurred"].historyAmount = [newCostInc];
        }
      }
    }

    const curYAs = new Set(curFirstRenoRows.map((item) => item.YA.value));

    const filteredFirstRows = first.filter((item) => !curYAs.has(item.YA));

    const mappedFirstRows = mapRows(filteredFirstRows);
    const hasCurrentYearRow = mappedFirstRows.some(
      (item) => item.YA.value === currentYear
    );

    if (!scheduleDetails.isSdg) {
      mappedFirstRows.map((item) => (item["costIncurred"].disabled = true));
    }

    const mergedFirstRows = hasCurrentYearRow
      ? mappedFirstRows
      : [...curFirstRenoRows, ...mappedFirstRows];

    setRows(mergedFirstRows);

    firstRows = mergedFirstRows;

    calculateCostIncurred(mergedFirstRows, setTotalCostIncurred);
    calculateQualifyingCost(
      mergedFirstRows,
      setRows,
      setTotalQualifyingCost,
      scheduleDetails.isSdg
    );
    calculateYearsBF(
      mergedFirstRows,
      setRows,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF,
      scheduleDetails.isSdg
    );

    // Handle second rows
    if (second.length > 0) {
      const mappedSecondRows = mapRows(second);
      setRowsSecondTable(mappedSecondRows);
      secondRows = mappedSecondRows;

      calculateCostIncurred(mappedSecondRows, setTotalSecondCostIncurred);
      calculateQualifyingCost(
        mappedSecondRows,
        setRowsSecondTable,
        setTotalSecondQualifyingCost,
        scheduleDetails.isSdg
      );
      calculateYearsBF(
        mappedSecondRows,
        setRowsSecondTable,
        setTotalSecondDeduction,
        setTotalSecondCostCF,
        setTotalSecondQualifyingCostBF,
        scheduleDetails.isSdg
      );
    }

    if (scheduleDetails.mode === "1x3 years claim") {
      setSelectedYears(firstRows.map((row) => row.YA.value));
    } else if (scheduleDetails.mode === "2x3 years claim") {
      setSelectedYears(
        [...firstRows, ...secondRows].map((row) => row.YA.value)
      );
    }

    columnSDGSettings[0].maxYear = currentYear;
    columnNoSDGSettings[0].maxYear = currentYear;
  }, []);

  const createEmptyRow = () => ({
    YA: { value: "" },
    start3Year: { value: false },
    exRate: { value: 1 },
    costIncurredFunc: { value: "", historyAmount: [] },
    costIncurred: { value: "", historyAmount: [], disabled: !isSdg },
    qualifyingCost: { value: "" },
    qualifyingCostFunc: { value: "" },
    qualifyingCostBF: { value: "" },
    yearsClaim: { value: "" },
    yearsBF: { value: "" },
    deduction: { value: "" },
    costCF: { value: "" },
    yearsCF: { value: "" },
    permitToAdd: false,
    permitToDelete: true,
  });

  const handleModeChange = (newMode) => {
    setMode(newMode);

    if (newMode === "2x3 years claim") {
      setRowsSecondTable([createEmptyRow()]);
    }
  };

  const handleAddRow = (setRowsFn) => {
    setRowsFn((prevRows) => [...prevRows, createEmptyRow()]);
  };

  const handleRemoveRow = (
    index,
    rows,
    setRowsFn,
    otherRows,
    setTotalCostIncurred,
    setTotalQualifyingCost,
    setTotalDeduction,
    setTotalCostCF,
    setTotalQualifyingCostBF
  ) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    setSelectedYears([...updatedRows, ...otherRows].map((row) => row.YA.value));

    // Recalculate values after removing row
    calculateCostIncurred(updatedRows, setTotalCostIncurred);
    calculateQualifyingCost(
      updatedRows,
      setRowsFn,
      setTotalQualifyingCost,
      isSdg
    );
    calculateYearsBF(
      updatedRows,
      setRowsFn,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF,
      isSdg
    );
  };

  // Usage for first table
  const addRow = () => handleAddRow(setRows);
  const removeRow = (index) =>
    handleRemoveRow(
      index,
      rows,
      setRows,
      rowsSecondTable,
      setTotalCostIncurred,
      setTotalQualifyingCost,
      setTotalDeduction,
      setTotalCostCF,
      setTotalQualifyingCostBF
    );

  // Usage for second table
  const addRowSecondTable = () => handleAddRow(setRowsSecondTable);
  const removeRowSecondTable = (index) =>
    handleRemoveRow(
      index,
      rowsSecondTable,
      setRowsSecondTable,
      rows,
      setTotalSecondCostIncurred,
      setTotalSecondQualifyingCost,
      setTotalSecondDeduction,
      setTotalSecondCostCF,
      setTotalSecondQualifyingCostBF
    );

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          rennovationRows={rennovationRows}
          mode={mode}
          isSdg={isSdg}
          tableRows={rows}
          secondTableRows={rowsSecondTable}
          totalRennovations={totalRennovations}
          totalQualifyingCost={totalQualifyingCost}
          totalQualifyingCostBF={totalQualifyingCostBF}
          totalCostIncurred={totalCostIncurred}
          totalDeduction={totalDeduction}
          totalCostCF={totalCostCF}
          totalSecondCostIncurred={totalSecondCostIncurred}
          totalSecondQualifyingCost={totalSecondQualifyingCost}
          totalSecondQualifyingCostBF={totalSecondQualifyingCostBF}
          totalSecondDeduction={totalSecondDeduction}
          totalSecondCostCF={totalSecondCostCF}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schedule.name}
        taxAdjustment={"Deduct"}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox title='Current Year R&R Cost Incurred'>
              <CommonTable
                rows={rennovationRows}
                columns={[
                  {
                    key: "description",
                    label: "Description",
                    headerAlign: "left",
                    colAlign: "left",
                    render: (row) => (
                      <TextFieldForViewOnly
                        value={row.description || "—"}
                        sx={{ textAlign: "left" }}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "center",
                    colAlign: "center",
                    render: (row, _) => (
                      <TextFieldForViewOnly
                        value={formatAmount(row.amount) || "—"}
                      />
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleRennovationInputChange}
                        reference={{
                          index: index,
                          field: "reference",
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || "—"} />
                    ),
                  },
                ]}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    fontSize: { xs: "1rem", md: "1.15rem" },
                  }}
                >
                  Total current year R&R cost incurred:&nbsp;
                </Typography>
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                  }}
                >
                  {formatAmount(totalRennovations)}
                </Typography>
              </Paper>
            </SectionBox>
            <Divider sx={{ marginY: 4 }} />
            {canEditSchedule && (
              <Box sx={{ mb: 3, pt: 1, px: 3 }}>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item>
                    <Typography variant='body1'>Select Claim Mode:</Typography>
                  </Grid>
                  <Grid item>
                    <JustDropDown
                      label=''
                      value={mode}
                      options={claimModeOptions}
                      onChange={(e) => handleModeChange(e.target.value)}
                      size='small'
                    />
                  </Grid>
                  <Grid item></Grid>
                  <Grid item>
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      Functional Currency is <strong>SGD</strong>
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Checkbox
                      checked={isSdg}
                      onChange={(e) =>
                        handleFuncCurrencyChange(e.target.checked)
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <SectionBox
              title='1x3 years claim'
              onAdd={() => addRow()}
              canEdit={canEditSchedule}
            >
              <RenovationTable
                rows={rows}
                isSdg={isSdg}
                selectedYears={selectedYears}
                handleInputChange={handleInputChange}
                handleCheckboxChange={handleCheckboxChange}
                totalCostIncurred={totalCostIncurred}
                totalQualifyingCost={totalQualifyingCost}
                totalQualifyingCostBF={totalQualifyingCostBF}
                totalDeduction={totalDeduction}
                totalCostCF={totalCostCF}
                addRow={addRow}
                removeRow={removeRow}
                canEdit={canEditSchedule}
                largeLayout={useLargeRenovationLayout}
              />
            </SectionBox>
            {mode === "2x3 years claim" && (
              <>
                <Divider sx={{ marginY: 2 }} />
                <SectionBox
                  title='2x3 years claim'
                  onAdd={() => addRowSecondTable()}
                  canEdit={canEditSchedule}
                >
                  <RenovationTable
                    rows={rowsSecondTable}
                    isSdg={isSdg}
                    selectedYears={selectedYears}
                    handleInputChange={handleSecondRowsInputChange}
                    handleCheckboxChange={handleSecondRowsCheckboxChange}
                    totalCostIncurred={totalSecondCostIncurred}
                    totalQualifyingCost={totalSecondQualifyingCost}
                    totalQualifyingCostBF={totalSecondQualifyingCostBF}
                    totalDeduction={totalSecondDeduction}
                    totalCostCF={totalSecondCostCF}
                    addRow={addRowSecondTable}
                    removeRow={removeRowSecondTable}
                    canEdit={canEditSchedule}
                    largeLayout={useLargeRenovationLayout}
                  />
                </SectionBox>
              </>
            )}
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default RenovationSchedule;
